import { useState, useEffect } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { ArrowRight, Search } from 'lucide-react'
import { supabase } from '../config/supabase'

export default function ProductsPage() {
  const [activeCategory, setActiveCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [categories, setCategories] = useState<any[]>([])
  const [products, setProducts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 获取产品分类
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('product_categories_s_762b1c2b_1')
          .select('*')
          .eq('is_deleted', 'n')

        if (categoriesError) {
          console.error('Error fetching categories:', categoriesError)
        } else {
          // 处理分类数据，添加 'all' 分类
          const processedCategories = [
            { id: 'all', name: '全部' },
            ...(categoriesData || []).map(category => ({
              id: category.id,
              name: category.name || '未命名分类'
            }))
          ]
          setCategories(processedCategories)
        }

        // 获取产品
        const { data: productsData, error: productsError } = await supabase
          .from('products_s_762b1c2b_1')
          .select('*')
          .eq('is_deleted', 'n')

        if (productsError) {
          console.error('Error fetching products:', productsError)
        } else {
          // 为产品添加默认属性，确保与原代码兼容
          const enhancedProducts = (productsData || []).map(product => {
            // 解析 specification 字段为 specs 对象
            let specs = product.specs || { power: '30-300kW', temp: '40-80℃', fuel: '生物质颗粒' }
            if (product.specification) {
              const specLines = product.specification.split('\n')
              const parsedSpecs: any = {}
              specLines.forEach((line: string) => {
                const [key, value] = line.split('：')
                if (key && value) {
                  const cleanKey = key.trim()
                  const cleanValue = value.trim()
                  if (cleanKey.includes('功率')) {
                    parsedSpecs.power = cleanValue
                  } else if (cleanKey.includes('温度')) {
                    parsedSpecs.temp = cleanValue
                  } else if (cleanKey.includes('燃料')) {
                    parsedSpecs.fuel = cleanValue
                  }
                }
              })
              if (Object.keys(parsedSpecs).length > 0) {
                specs = parsedSpecs
              }
            }
            
            return {
              ...product,
              image: product.cover_image || `https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=${encodeURIComponent(product.name || 'industrial heater')}&image_size=square`,
              features: typeof product.features === 'string' ? product.features.split(',') : (product.features || ['高效节能', '自动控温', '智能恒温保护']),
              specs: specs,
              price: product.price || '¥0起',
              detailLink: product.detailLink || '#'
            }
          })
          
          // 按产品编号正序排序（优先使用 product_code 或 model_code 或 id 排序）
          const sortedProducts = enhancedProducts.sort((a, b) => {
            const getSortValue = (product: any) => {
              return product.product_code || product.model_code || product.id || ''
            }
            const valA = getSortValue(a)
            const valB = getSortValue(b)
            
            // 如果是数字类型，按数字比较
            if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
              return Number(valA) - Number(valB)
            }
            // 否则按字符串比较
            return String(valA).localeCompare(String(valB))
          })
          
          setProducts(sortedProducts)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const filteredProducts = products.filter(product => {
    let matchesCategory = activeCategory === 'all';
    if (!matchesCategory) {
      // 查找分类 ID 对应的分类名称，然后与产品的 category 字段比较
      const category = categories.find(cat => cat.id === activeCategory);
      if (category && product.category === category.name) {
        matchesCategory = true;
      }
    }
    const matchesSearch = searchQuery === '' || product.name.toLowerCase().includes(searchQuery.toLowerCase()) || product.description.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-primary-700 to-primary-900 text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent-orange/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-4">
              产品中心
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">产品中心</h1>
            <p className="text-xl text-white/80">探索云净炉业的优质产品</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-4">
            <div className="w-full md:w-1/3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="搜索产品..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 pl-10 rounded-lg border border-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <Search className="w-5 h-5 text-secondary-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === category.id ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'}`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              <div className="col-span-full flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="col-span-full text-center py-16">
                <p className="text-secondary-500">暂无产品数据</p>
              </div>
            ) : (
              filteredProducts.map((product) => (
                <div key={product.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-secondary-100">
                  <div className="aspect-square overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                  <div className="p-6">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${product.category === 'boiler' ? 'bg-blue-100 text-blue-800' : product.category === 'heater' ? 'bg-red-100 text-red-800' : product.category === 'fireplace' ? 'bg-orange-100 text-orange-800' : product.category === 'burner' ? 'bg-purple-100 text-purple-800' : product.category === 'granule' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {categories.find(c => c.id === product.category)?.name}
                    </span>
                    <h3 className="text-xl font-bold text-secondary-900 mb-3">
                      {product.name}
                    </h3>
                    <p className="text-secondary-600 mb-4 line-clamp-3">
                      {product.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {product.features.map((feature: string, index: number) => (
                        <span key={index} className="px-3 py-1 bg-secondary-50 text-secondary-700 rounded-full text-xs font-medium">
                          {feature}
                        </span>
                      ))}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-primary-600">
                        {product.price}
                      </span>
                      <a 
                        href={product.category === '颗粒炉' ? '/air-granule-models?category=颗粒炉' : product.category === '水暖炉' ? '/air-granule-models?category=水暖炉' : product.category === '燃木壁炉' ? '/air-granule-models?category=燃木壁炉' : product.category === '燃烧机' ? '/air-granule-models?category=燃烧机' : '#'}
                        className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                      >
                        <span>查看详情</span>
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          <div className="mt-12 flex justify-center">
            <div className="inline-flex rounded-md shadow">
              <button className="px-4 py-2 border border-secondary-200 bg-white text-secondary-500 hover:bg-secondary-50 rounded-l-md">
                上一页
              </button>
              <button className="px-4 py-2 border-t border-b border-secondary-200 bg-primary-600 text-white">
                1
              </button>
              <button className="px-4 py-2 border-t border-b border-secondary-200 bg-white text-secondary-700 hover:bg-secondary-50">
                2
              </button>
              <button className="px-4 py-2 border border-secondary-200 bg-white text-secondary-500 hover:bg-secondary-50 rounded-r-md">
                下一页
              </button>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}
